import React, { useEffect, useState, useRef } from "react";
//prettier-ignore
import { 
  TextField, 
  FormControlLabel, 
  Radio, 
  RadioGroup, 
  FormControl, 
  Container, 
  CircularProgress, 
  Alert, 
  Grid, 
  Select, 
  MenuItem, 
  InputLabel,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Chip,
  Divider,
  Snackbar
} from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { 
  Search as SearchIcon, 
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from "@mui/icons-material";
import FormContainer from "../../../../components/Forms/FormContainer";
import { 
  ExecuteApiToPost, 
  GetDataFromApiWithParams,
  UpdateAndGetResponse,
  DeleteAndGetResponse
} from "../../../../services/api/ExecuteApiRequests";
import { GetAgreements } from "../../Shared/CommonData/CommonData";
import useDecodedUser from "../../../../services/hooks/useDecodedUser";

// Alert component for toast notifications
const AlertComponent = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Initial data with employment_data properly structured including present totals
const initialData = { 
  id: "",
  park_id: "", 
  investor_id: "", 
  agreement_id: "", 
  activity_period_start_from: null,
  activity_period_to: null, 
  export_amount: 0,
  import_substitution: 0,
  import: 0,
  linkage: "0",
  linkage_money: 0,
  description: "",
  employment_data: {
    professional_male: 0,
    professional_female: 0,
    labor_male: 0,
    labor_female: 0,
    present_professional_male: 0,
    present_professional_female: 0,
    present_labor_male: 0,
    present_labor_female: 0
  },
  status: "Request",
  amount: 0,
  activity_type: ""
};

// Create a TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`activity-tabpanel-${index}`}
      aria-labelledby={`activity-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// EditableTableCell component for inline editing
const EditableTableCell = ({ record, field, value, isEditing, onFieldChange, fieldType = "text" }) => {
  if (isEditing) {
    // For date fields
    if (fieldType === "date") {
      return (
        <TableCell>
          <TextField
            size="small"
            type="date"
            value={value ? new Date(value).toISOString().split('T')[0] : ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: '100%' }}
          />
        </TableCell>
      );
    }
    
    // For numeric fields
    if (fieldType === "number") {
      return (
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={value || 0}
            onChange={(e) => onFieldChange(field, parseInt(e.target.value) || 0)}
            sx={{ width: '80px' }}
            inputProps={{ min: 0 }}
          />
        </TableCell>
      );
    }
    
    // For text fields
    if (fieldType === "text") {
      return (
        <TableCell>
          <TextField
            size="small"
            value={value || ''}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '100%' }}
            multiline={field === "description"}
            rows={field === "description" ? 2 : 1}
          />
        </TableCell>
      );
    }
    
    // For status field (select)
    if (fieldType === "status") {
      return (
        <TableCell>
          <Select
            size="small"
            value={value || 'Request'}
            onChange={(e) => onFieldChange(field, e.target.value)}
            sx={{ width: '120px' }}
          >
            <MenuItem value="Request">Request</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
          </Select>
        </TableCell>
      );
    }
    
    // Default to regular cell for non-editable fields
    return <TableCell>{value}</TableCell>;
  }
  
  // Non-editable mode - display values
  if (fieldType === "date" && value) {
    return <TableCell>{new Date(value).toLocaleDateString()}</TableCell>;
  }
  
  if (fieldType === "number") {
    return <TableCell>{value || 0}</TableCell>;
  }
  
  return <TableCell>{value || ''}</TableCell>;
};

export default function RegisterInvestorActivities() {
  const parktextFieldRef = useRef(null);
  const [formData, setFormData] = useState(initialData);
  const [investorsInPark, setInvestorInPark] = useState([]);
  const [selectedPark, setSelectedPark] = useState({ name: '' });
  const [agreements, setAgreements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Toast Notification States
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  
  // Separate state variables for main tabs and activity tabs
  const [mainTabValue, setMainTabValue] = useState(0);
  const [activityTabValue, setActivityTabValue] = useState(0);
  
  const [parkIdState, setParkIdState] = useState("");
  
  // Edit Tab States
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Inline editing states - using 'no' as identifier
  const [editingId, setEditingId] = useState(null);
  const [editedRowData, setEditedRowData] = useState(null);
  const [originalRowData, setOriginalRowData] = useState(null);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // NEW STATE: To store the selected investor ID separately
  const [inverstorId, setInverstorId] = useState("");
  
  const decodedUser = useDecodedUser();
  
  // Map activity types to tab indices
  const activityTypes = [
    "export", 
    "import_substitution", 
    "import", 
    "employment", 
    "linkage"
  ];
  
  const activityLabels = [
    "Export", 
    "Import Substitution", 
    "Import", 
    "Employment", 
    "Linkage"
  ];
  
  const mainTabs = ["Register Activity", "Edit Activity"];

  // Toast Notification Functions
  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  };

  // Show success toast function
  const showSuccessToast = (message) => {
    showToast(message, "success");
  };

  // Show error toast function
  const showErrorToast = (message) => {
    showToast(message, "error");
  };

  // Show warning toast function
  const showWarningToast = (message) => {
    showToast(message, "warning");
  };

  // Show info toast function
  const showInfoToast = (message) => {
    showToast(message, "info");
  };

  useEffect(() => {
    const fetchAgreementList = async () => {
      try {
        const parkId = decodedUser.park_id;
        setParkIdState(parkId);
        const getSelectedPark = await GetDataFromApiWithParams(`parks/${parkId}`);
        
        console.log("Park data:", getSelectedPark);

        if (getSelectedPark) {
          const parkId = getSelectedPark.id;
          
          // Update formData while preserving employment_data
          setFormData(prev => ({
            ...prev,
            park_id: parkId,
            investor_id: "" // Reset investor_id
          }));
          
          // Also reset the separate investorId state
          setInverstorId("");
          
          const responseData = await GetAgreements(parkId); 
          setAgreements(responseData);
          //************debuging */
          console.log("=== INVESTOR DROPDOWN DEBUG ===");
          console.log("selectedPark.name (from /parks/:id):", JSON.stringify(getSelectedPark.name));
          console.log("responseData (raw agreements from GetAgreements):", responseData);
          console.log("park_name values returned per agreement:", responseData.map(a => ({
            company_name: a.company_name,
            park_name: a.park_name,
            matches: a.park_name === getSelectedPark.name
          })));
          console.log("================================SSSSSSSSSSSSS");
          //****************** */
          const filteredInvestors = [
            ...new Set(
              responseData
                .filter((agreement) => agreement.park_name === getSelectedPark.name)
                .map((agreement) => agreement.company_name),
            ),
          ].map((company_name) => responseData.find((agreement) => agreement.company_name === company_name));
          setInvestorInPark(filteredInvestors);
          console.log("rrrrrrrrrrrrrrrrrrrrrrrrrrrr")
          // Show success toast
          showSuccessToast("Park data loaded successfully!");
        }
        setSelectedPark(getSelectedPark);
      } catch (err) {
        console.log(err);
        showErrorToast("Failed to load park data!");
      }
    };
    fetchAgreementList();
  }, [decodedUser]);

  const fetchAllActivities = async () => {
    try {
      setIsSearching(true);
      const response = await GetDataFromApiWithParams(`investors/activities?park_id=${parkIdState}&page=${page + 1}&limit=${rowsPerPage}`);
      if (response) {
        if (Array.isArray(response)) {
          setSearchResults(response);
          setTotalRecords(response.length);
        } else if (response.data && Array.isArray(response.data)) {
          setSearchResults(response.data);
          setTotalRecords(response.total || response.data.length);
        } else {
          setSearchResults([]);
          setTotalRecords(0);
        }
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
      setSearchResults([]);
      setTotalRecords(0);
      showErrorToast("Failed to load activities!");
    } finally {
      setIsSearching(false);
    }
  };

  const handleMainTabChange = (event, newValue) => {
    setMainTabValue(newValue);
    if (newValue === 0) {
      // Reset form when switching to register tab
      setFormData(initialData);
      setInverstorId("");
      setEditMode(false);
      // Exit inline edit mode
      setEditingId(null);
      setEditedRowData(null);
      setOriginalRowData(null);
    }
  };

  const handleActivityTabChange = (event, newValue) => {
    if (mainTabValue === 0) {
      setActivityTabValue(newValue);
      setFormData(prev => ({ 
        ...prev,
        activity_type: activityTypes[newValue] 
      }));
      // showInfoToast(`Selected activity type: ${activityLabels[newValue]}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "investor_id") {
      setInverstorId(value);
      setFormData(prev => ({ 
        ...prev,
        investor_id: value 
      }));
      
      if (value) {
        const selectedInvestor = investorsInPark.find(inv => inv.company_id === value);
        if (selectedInvestor) {
          showInfoToast(`Selected investor: ${selectedInvestor.company_name}`);
        }
      }
    } else {
      setFormData(prev => ({ 
        ...prev,
        [name]: value 
      }));
    }
  };

  const handleInvestorSelect = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setInverstorId(value);
    setFormData(prev => ({ 
      ...prev,
      investor_id: value 
    }));
    
    if (value) {
      const selectedInvestor = investorsInPark.find(inv => inv.company_id === value);
      if (selectedInvestor) {
        // showInfoToast(`Selected investor: ${selectedInvestor.company_name}`);
      }
    }
  };

  // Search function for edit tab
  const handleSearch = async () => {
    try {
      setIsSearching(true);
      
      console.log("In search investor activity: ", parkIdState, "search qury:", searchQuery)
     
      const response = await GetDataFromApiWithParams("investors/searchactivity", [parkIdState, searchQuery]);
     
      
      setSearchResults(response);
      setPage(0);
      console.log("other search result", searchResults);
      
      const resultCount = response?.length || 0;
      if (resultCount > 0) {
        showSuccessToast(`Found ${resultCount} activity records!`);
      } else {
        showWarningToast("No records found for your search.");
      }
    } catch (error) {
      console.error("Error searching activities:", error);
      setSearchResults([]);
      setTotalRecords(0);
      showErrorToast("Search failed! Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  // Handle enter key in search
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Load record for editing in form
  const handleEditRecord = async (record) => {
    try {
      setIsLoading(true);
      showInfoToast("Loading record for editing...");
      
      const response = await GetDataFromApiWithParams(`investors/activities/${record.no}`);
      
      if (response) {
        setFormData({
          ...response,
          activity_period_start_from: response.activity_period_start_from ? response.activity_period_start_from.split('T')[0] : null,
          activity_period_to: response.activity_period_to ? response.activity_period_to.split('T')[0] : null,
          employment_data: response.employment_data || initialData.employment_data
        });
        
        setInverstorId(response.investor_id);
        setEditMode(true);
        
        const activityIndex = activityTypes.indexOf(response.activity_type);
        if (activityIndex !== -1) {
          setActivityTabValue(activityIndex);
        }
        
        setMainTabValue(0);
        
        showSuccessToast("Record loaded successfully! You can now edit it in the form.");
      }
    } catch (error) {
      console.error("Error loading record:", error);
      showErrorToast("Failed to load record for editing!");
    } finally {
      setIsLoading(false);
    }
  };

  // Start inline editing
  const handleInlineEdit = (record) => {
    // If we're already editing a different row, cancel that edit first
    if (editingId && editingId !== record.no) {
      handleCancelInlineEdit();
    }
    
    setEditingId(record.no);
    setOriginalRowData({...record});
    setEditedRowData({...record});
    
    showInfoToast(`Editing record #${record.no}. Click save to save changes or cancel to discard.`);
  };

  // Save inline edits - IMPROVED VERSION
  const handleSaveInlineEdit = async () => {
    if (!editedRowData) {
      showErrorToast("No data to save!");
      return;
    }
    
    try {
      setIsLoading(true);
      //showInfoToast("Saving changes...");
      
      // Prepare the data for update
      const updateData = { ...editedRowData };
      
      // console.log("Data to update:", updateData);
      // console.log("Updating record ID (no):", editingId);
      
      // Remove unwanted fields
      const fieldsToRemove = ['company_name', 'recorded_on', 'created_at', 'updated_at'];
      const cleanData = { ...updateData };
      fieldsToRemove.forEach(field => delete cleanData[field]);
      
      // Format dates if they exist
      if (cleanData.activity_period_start_from) {
        cleanData.activity_period_start_from = 
          new Date(cleanData.activity_period_start_from).toISOString().split('T')[0];
      }
      
      if (cleanData.activity_period_to) {
        cleanData.activity_period_to = 
          new Date(cleanData.activity_period_to).toISOString().split('T')[0];
      }
      
      console.log("Cleaned data for API:", cleanData);
      
      // Call API with the clean data
      const result = await UpdateAndGetResponse(`investors/activities/${editingId}`, cleanData);
      
      if (result) {
        // Update the search results locally
        setSearchResults(prevResults => 
          prevResults.map(record => 
            record.no === editingId ? { ...record, ...cleanData } : record
          )
        );
        
        // Exit edit mode
        setEditingId(null);
        setEditedRowData(null);
        setOriginalRowData(null);
        
        showSuccessToast("Record updated successfully!");
      } else {
        showErrorToast("Failed to save changes. No response from server.");
      }
    } catch (error) {
      console.error("Error saving record:", error);
      showErrorToast("Failed to save changes! Error: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel inline editing
  const handleCancelInlineEdit = () => {
    setEditingId(null);
    setEditedRowData(null);
    setOriginalRowData(null);
    showInfoToast("Edit cancelled");
  };

  // Handle field changes in inline editing
  const handleFieldChange = (fieldName, value) => {
    setEditedRowData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Delete record
  const handleDeleteRecord = async (recordId) => {
    
    if (window.confirm("Are you sure you want to delete this activity record?")) {
      try {
        setIsLoading(true);
        showInfoToast("Deleting activity record...");
        
        await DeleteAndGetResponse(`investors/deleteinverstoactivity/${recordId}`);
        
        // Update search results
        setSearchResults(prevResults => prevResults.filter(record => record.no !== recordId));
        
        // If we're deleting the record that's being edited, cancel edit mode
        if (editingId === recordId) {
          setEditingId(null);
          setEditedRowData(null);
          setOriginalRowData(null);
        }
        
        showSuccessToast("Activity record deleted successfully!");
      } catch (error) {
        console.error("Error deleting record:", error);
        showErrorToast("Failed to delete activity record!");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // View record details
  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setViewDialogOpen(true);
    showInfoToast(`Viewing details for record #${record.no}`);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRecord(null);
  };

  // Safe access to employment_data with fallback to initial values
  const getEmploymentData = () => {
    return formData.employment_data || {
      professional_male: 0,
      professional_female: 0,
      labor_male: 0,
      labor_female: 0,
      present_professional_male: 0,
      present_professional_female: 0,
      present_labor_male: 0,
      present_labor_female: 0
    };
  };

  const handleEmploymentChange = (category, gender, value, isPresent = false) => {
    const numericValue = parseInt(value) || 0;
    const currentEmploymentData = getEmploymentData();
    const fieldName = isPresent ? `present_${category}_${gender}` : `${category}_${gender}`;
    
    setFormData(prev => ({
      ...prev,
      employment_data: {
        ...currentEmploymentData,
        [fieldName]: numericValue
      },
      amount: calculateTotalEmployment()
    }));
    
    if (numericValue > 100) {
      showInfoToast(`${category.charAt(0).toUpperCase() + category.slice(1)} ${gender} employment set to ${numericValue}`);
    }
  };

  const calculateTotalEmployment = () => {
    const employmentData = getEmploymentData();
    return (
      (employmentData.professional_male || 0) +
      (employmentData.professional_female || 0) +
      (employmentData.labor_male || 0) +
      (employmentData.labor_female || 0)
    );
  };

  const calculatePresentTotalEmployment = () => {
    const employmentData = getEmploymentData();
    return (
      (employmentData.present_professional_male || 0) +
      (employmentData.present_professional_female || 0) +
      (employmentData.present_labor_male || 0) +
      (employmentData.present_labor_female || 0)
    );
  };

  const calculateProfessionalCreatedTotal = () => {
    const employmentData = getEmploymentData();
    return (employmentData.professional_male || 0) + (employmentData.professional_female || 0);
  };

  const calculateProfessionalPresentTotal = () => {
    const employmentData = getEmploymentData();
    return (employmentData.present_professional_male || 0) + (employmentData.present_professional_female || 0);
  };

  const calculateLaborCreatedTotal = () => {
    const employmentData = getEmploymentData();
    return (employmentData.labor_male || 0) + (employmentData.labor_female || 0);
  };

  const calculateLaborPresentTotal = () => {
    const employmentData = getEmploymentData();
    return (employmentData.present_labor_male || 0) + (employmentData.present_labor_female || 0);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    
    if (!inverstorId) {
      showErrorToast("Please select an investor!");
      setIsLoading(false);
      return;
    }
    
    if (!formData.activity_period_start_from || !formData.activity_period_to) {
      showErrorToast("Please select activity period dates!");
      setIsLoading(false);
      return;
    }
    
    if (formData.activity_period_start_from > formData.activity_period_to) {
      showErrorToast("Start date cannot be after end date!");
      setIsLoading(false);
      return;
    }
    
    let tabSpecificError = false;
    let errorMessage = "";
    
    const selectedActivityType = activityTypes[activityTabValue];
    const currentActivityType = formData.activity_type || selectedActivityType;
    
    switch(currentActivityType) {
      case "export":
        if (formData.export_amount === undefined || formData.export_amount === null || formData.export_amount < 0) {
          errorMessage = "Export amount must be 0 or greater!";
          tabSpecificError = true;
        }
        break;
      // case "import_substitution":
      //   // if (formData.Import_substitution === undefined || formData.import_substitution === null || formData.import_substitution < 0) {
      //   if (  formData.import_substitution < 0) {
      //   errorMessage = "Import substitution amount must be 0 or greater!";
      //     tabSpecificError = true;
      //   }
        break;
      case "import":
        if (formData.import === undefined || formData.import === null || formData.import < 0) {
          errorMessage = "Import amount must be 0 or greater!";
          tabSpecificError = true;
        }
        break;
      case "employment":
        const totalEmployment = calculateTotalEmployment();
        if (totalEmployment < 0) {
          errorMessage = "Employment total cannot be negative!";
          tabSpecificError = true;
        }
        break;
      case "linkage":
        if (formData.linkage_money === undefined || formData.linkage_money === null || formData.linkage_money < 0) {
          errorMessage = "Linkage amount must be 0 or greater!";
          tabSpecificError = true;
        }
        break;
      default:
        errorMessage = "Please select an activity type!";
        tabSpecificError = true;
    }
    
    if (tabSpecificError) {
      showErrorToast(errorMessage);
      setIsLoading(false);
      return;
    }
    
    try {
      showInfoToast(editMode ? "Updating activity record..." : "Submitting activity registration...");
      
      const submissionData = {
        ...formData,
        park_id: parkIdState,
        investor_id: inverstorId,
        employment_data: getEmploymentData(),
        activity_type: currentActivityType
      };
      
      console.log("Submitting data:", submissionData);
      
      let resultData;
      if (editMode) {
        resultData = await UpdateAndGetResponse(`investors/activities/${formData.no}`, submissionData);
        showSuccessToast("✅ Investor activity updated successfully!");
      } else {
        resultData = await ExecuteApiToPost("investors/activities", submissionData);
        showSuccessToast("✅ Investor activity registered successfully!");
      }

      if (resultData) {
        setFormData(initialData);
        setInverstorId("");
        setActivityTabValue(0);
        setEditMode(false);
        
        if (mainTabValue === 1) {
          handleSearch();
        }
      }
    } catch (error) {
      showErrorToast("❌ An error occurred. Please try again!");
      console.error("Submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form
  const handleResetForm = () => {
    setFormData(initialData);
    setInverstorId("");
    setEditMode(false);
    setActivityTabValue(0);
    showInfoToast("Form reset to initial state");
  };

  // Refresh activities list
  const handleRefreshActivities = () => {
    handleSearch();
    showInfoToast("Refreshing activities list...");
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    showInfoToast(`Showing ${event.target.value} records per page`);
  };

  // Get safe employment data for rendering
  const employmentData = getEmploymentData();

  // Get investor name from ID
  const getInvestorName = (investorId) => {
    const investor = investorsInPark.find(inv => inv.company_id === investorId);
    return investor ? investor.company_name : investorId;
  };

  return (
    <Container>
      {/* Toast Notification */}
      <Snackbar 
        open={toastOpen} 
        autoHideDuration={6000} 
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <AlertComponent 
          onClose={handleToastClose} 
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </AlertComponent>
      </Snackbar>

      {/* Main Tabs for Register/Edit */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={mainTabValue} 
          onChange={handleMainTabChange}
          aria-label="main tabs"
          variant="fullWidth"
        >
          {mainTabs.map((label, index) => (
            <Tab 
              key={index}
              label={label} 
              id={`main-tab-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Register Activity Tab */}
      {mainTabValue === 0 && (
        <>
          {editMode && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="h6" color="warning.dark">
                ✏️ Editing Activity Record #{formData.no}
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={handleResetForm}
                sx={{ mt: 1 }}
              >
                Cancel Edit
              </Button>
            </Box>
          )}

          <FormContainer 
            title={
              <span style={{ 
                fontFamily: "'Times New Roman', Times, serif", 
                fontSize: '20px', 
                textAlign: 'left',
                color: '#000080', 
                fontWeight: 100 
              }}>
                {editMode ? "Edit Investor Activity" : "Register Investor Activities"}
              </span>
            } 
            onSubmit={handleSubmit}
          >
            <Grid item xs={12} md={12}>
              <TextField 
                label="Park name"
                inputRef={parktextFieldRef}
                value={selectedPark?.name || ''}
                disabled
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth={true} required>
                <InputLabel id="investor-label">Select Investor</InputLabel>
                <Select
                  labelId="investor-label"
                  id="investor_id"
                  name="investor_id"
                  color="info"
                  value={inverstorId}
                  fullWidth={true}
                  onChange={handleInvestorSelect}
                >
                  <MenuItem value="">Select Investor</MenuItem>
                  {investorsInPark?.length > 0 &&
                    investorsInPark.map((inv, index) => (
                      <MenuItem key={index} value={inv.company_id}>
                        {inv.company_name} ({inv.company_id})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Display selected investor information */}
            {/* {inverstorId && (
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 1, 
                  bgcolor: 'info.light', 
                  borderRadius: 1,
                  color: 'white',
                  mt: 1
                }}>
                  <Typography variant="body2">
                    Selected Investor ID: <strong>{inverstorId}</strong>
                  </Typography>
                </Box>
              </Grid>
            )} */}
            
            <Grid item xs={12} md={6}>
              {inverstorId !== "" && (
                <>
                  <span>UPINs: </span>
                  {agreements?.map((agr) => agr.company_id === inverstorId && <span>[ {agr.upin} ], </span>)}
                </>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Starting from"
                name="activity_period_start_from"
                type="date"
                fullWidth={true}
                value={formData.activity_period_start_from}
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="To"
                name="activity_period_to"
                type="date"
                fullWidth={true}
                value={formData.activity_period_to}
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
                required
              />
            </Grid>
            
            {/* Activity Type Tabs */}
            <Grid item xs={12} md={12}>
              <FormControl component="fieldset" fullWidth={true} required>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                      value={activityTabValue} 
                      onChange={handleActivityTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      aria-label="activity type tabs"
                    >
                      {activityLabels.map((label, index) => (
                        <Tab 
                          key={index}
                          label={label} 
                          id={`activity-tab-${index}`}
                          aria-controls={`activity-tabpanel-${index}`}
                        />
                      ))}
                    </Tabs>
                  </Box>
                  
                  {/* Tab Panels with specific content for each tab */}
                  <TabPanel value={activityTabValue} index={0}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <TextField
                          label="Amount in USD or Quantity"
                          name="export_amount"
                          fullWidth={true}
                          value={formData.export_amount}
                          InputLabelProps={{ shrink: true }}
                          onChange={handleChange}
                          required
                          helperText="Enter export amount in USD"
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>
                  
                  <TabPanel value={activityTabValue} index={1}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <TextField
                          label="Amount in USD"
                          name="import_substitution"
                          fullWidth={true}
                          value={formData.import_substitution}
                          InputLabelProps={{ shrink: true }}
                          onChange={handleChange}
                          required
                          helperText="Enter import substitution amount or quantity"
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>
                  
                  <TabPanel value={activityTabValue} index={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <TextField
                          label="Amount in ETB"
                          name="import"
                          fullWidth={true}
                          value={formData.import}
                          InputLabelProps={{ shrink: true }}
                          onChange={handleChange}
                          required
                          helperText="Enter import amount in USD"
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>
                  
                  <TabPanel value={activityTabValue} index={3}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 3,
                        mt: 2,
                        mb: 3,
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                          <Typography variant="h6" gutterBottom>
                            Employment Details
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Enter number of employees by category and gender
                          </Typography>
                          
                          <TableContainer component={Paper} sx={{ mt: 2, mb: 3 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                  <TableCell rowSpan={2}>
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Category
                                    </span>
                                  </TableCell>
                                  <TableCell align="center" colSpan={3}>
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Created
                                    </span>
                                  </TableCell>
                                  <TableCell align="center" colSpan={3}>
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Present
                                    </span>
                                  </TableCell>
                                </TableRow>
                                
                                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                  <TableCell align="center">
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Male
                                    </span>
                                  </TableCell>
                                  <TableCell align="center">
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Female
                                    </span>
                                  </TableCell>
                                  <TableCell align="center">
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Total
                                    </span>
                                  </TableCell>
                                  
                                  <TableCell align="center">
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Male
                                    </span>
                                  </TableCell>
                                  <TableCell align="center">
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Female
                                    </span>
                                  </TableCell>
                                  <TableCell align="center">
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Total
                                    </span>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell component="th" scope="row">
                                    Professional
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={employmentData.professional_male || 0}
                                      onChange={(e) => handleEmploymentChange('professional', 'male', e.target.value, false)}
                                      inputProps={{ 
                                        min: 0,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={employmentData.professional_female || 0}
                                      onChange={(e) => handleEmploymentChange('professional', 'female', e.target.value, false)}
                                      inputProps={{ 
                                        min: 0,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {calculateProfessionalCreatedTotal()}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={employmentData.present_professional_male || 0}
                                      onChange={(e) => handleEmploymentChange('professional', 'male', e.target.value, true)}
                                      inputProps={{ 
                                        min: 0,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={employmentData.present_professional_female || 0}
                                      onChange={(e) => handleEmploymentChange('professional', 'female', e.target.value, true)}
                                      inputProps={{ 
                                        min: 0,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {calculateProfessionalPresentTotal()}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                
                                <TableRow>
                                  <TableCell component="th" scope="row">
                                    Labor
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={employmentData.labor_male || 0}
                                      onChange={(e) => handleEmploymentChange('labor', 'male', e.target.value, false)}
                                      inputProps={{ 
                                        min: 0,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={employmentData.labor_female || 0}
                                      onChange={(e) => handleEmploymentChange('labor', 'female', e.target.value, false)}
                                      inputProps={{ 
                                        min: 0,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {calculateLaborCreatedTotal()}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={employmentData.present_labor_male || 0}
                                      onChange={(e) => handleEmploymentChange('labor', 'male', e.target.value, true)}
                                      inputProps={{ 
                                        min: 0,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={employmentData.present_labor_female || 0}
                                      onChange={(e) => handleEmploymentChange('labor', 'female', e.target.value, true)}
                                      inputProps={{ 
                                        min: 0,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {calculateLaborPresentTotal()}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                
                                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                  <TableCell component="th" scope="row">
                                    <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                      Total
                                    </span>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {(employmentData.professional_male || 0) + (employmentData.labor_male || 0)}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {(employmentData.professional_female || 0) + (employmentData.labor_female || 0)}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {calculateTotalEmployment()}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {(employmentData.present_professional_male || 0) + (employmentData.present_labor_male || 0)}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {(employmentData.present_professional_female || 0) + (employmentData.present_labor_female || 0)}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography>
                                      <span style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12px', color: 'black', fontWeight: 300 }}>
                                        {calculatePresentTotalEmployment()}
                                      </span>
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      </Grid>
                    </Box>
                  </TabPanel>
                  
                  <TabPanel value={activityTabValue} index={4}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <TextField
                          label="Linkage Value in USD"
                          name="linkage_money"
                          fullWidth={true}
                          value={formData.linkage_money}
                          InputLabelProps={{ shrink: true }}
                          onChange={handleChange}
                          required
                          helperText="Enter linkage value or quantity"
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>
                </Box>

                <Grid item xs={12} md={12}>
                  <TextField
                    label="Description"
                    name="description"
                    fullWidth={true}
                    multiline
                    rows={4}
                    sx={{ mb: 2 }}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe the activity..."
                  />
                </Grid>
              </FormControl>
            </Grid>
          </FormContainer>
          
          {isLoading && <CircularProgress />}
        </>
      )}

      {/* Edit Activity Tab */}
      {mainTabValue === 1 && (
        <Box>
          <Typography variant="h5" gutterBottom>
            Edit Investor Activities
          </Typography>
          
          {/* Search Section */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search by Investor Name, ID, Activity Type, or Description"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={isSearching}
                  startIcon={<SearchIcon />}
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRefreshActivities}
                  startIcon={<RefreshIcon />}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Results Section */}
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>No</TableCell>
                    <TableCell>Investor</TableCell>
                    <TableCell>Investor ID</TableCell>
                    <TableCell>Park ID</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Export</TableCell>
                    <TableCell>Import</TableCell>
                    <TableCell>Import Substitution</TableCell>
                    <TableCell>Linkage</TableCell>
                    <TableCell>Prof Male</TableCell> 
                    <TableCell>Prof Female</TableCell> 
                    <TableCell>Labor Male</TableCell> 
                    <TableCell>Labor Female</TableCell> 
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isSearching ? (
                    <TableRow>
                      <TableCell colSpan={17} align="center">
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Loading records...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : searchResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={17} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No records found. {searchQuery ? 'Try a different search term.' : 'Start by searching or all records will appear.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    searchResults.map((record) => (
                      <TableRow 
                        key={record.no || record.investor_id} 
                        hover
                        sx={{ 
                          backgroundColor: editingId === record.no ? 'action.selected' : 'inherit',
                          '&:hover': {
                            backgroundColor: editingId === record.no ? 'action.selected' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell>{record.no}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {getInvestorName(record.investor_id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {record.investor_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                           {record.park_id}
                        </TableCell>
                        
                        {/* Editable cells - using record.no for comparison */}
                        <EditableTableCell
                          record={record}
                          field="activity_period_start_from"
                          value={editingId === record.no ? editedRowData?.activity_period_start_from : record.activity_period_start_from}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="date"
                        />
                        
                        <EditableTableCell
                          record={record}
                          field="activity_period_to"
                          value={editingId === record.no ? editedRowData?.activity_period_to : record.activity_period_to}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="date"
                        />
                        
                        <EditableTableCell
                          record={record}
                          field="description"
                          value={editingId === record.no ? editedRowData?.description : record.description}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="text"
                        />
                        
                        <EditableTableCell
                          record={record}
                          field="export_amount"
                          value={editingId === record.no ? editedRowData?.export_amount : record.export_amount}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="number"
                        />
                        
                        {/* FIXED: Changed "import" to "import_amount" */}
                        <EditableTableCell
                          record={record}
                          field="import"
                          value={editingId === record.no ? editedRowData?.import : record.import}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="number"
                        />
                        
                        {/* FIXED: Changed "Import_substitution" to "Import_Substitution" (case-sensitive!) */}
                        <EditableTableCell
                          record={record}
                          field="import_substitution"
                          value={editingId === record.no ? editedRowData?.import_substitution : record.import_substitution}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="number"
                        />
                        
                        {/* FIXED: Changed "linkage_money" to "linkage_amount" */}
                        <EditableTableCell
                          record={record}
                          field="linkage_money"
                          value={editingId === record.no ? editedRowData?.linkage_money : record.linkage_money}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="number"
                        />
                        
                        <EditableTableCell
                          record={record}
                          field="emp_prof_male"
                          value={editingId === record.no ? editedRowData?.emp_prof_male : record.emp_prof_male}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="number"
                        />
                        
                        <EditableTableCell
                          record={record}
                          field="emp_prof_female"
                          value={editingId === record.no ? editedRowData?.emp_prof_female : record.emp_prof_female}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="number"
                        />
                        
                        <EditableTableCell
                          record={record}
                          field="emp_labor_male"
                          value={editingId === record.no ? editedRowData?.emp_labor_male : record.emp_labor_male}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="number"
                        />
                        
                        <EditableTableCell
                          record={record}
                          field="emp_labor_female"
                          value={editingId === record.no ? editedRowData?.emp_labor_female : record.emp_labor_female}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="number"
                        />
                        
                        <EditableTableCell
                          record={record}
                          field="status"
                          value={editingId === record.no ? editedRowData?.status : record.status}
                          isEditing={editingId === record.no}
                          onFieldChange={handleFieldChange}
                          fieldType="status"
                        />
                        
                        <TableCell align="center">
                          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                            {editingId === record.no ? (
                              // Show Save and Cancel buttons when in edit mode
                              <>
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={handleSaveInlineEdit}
                                  title="Save Changes"
                                  disabled={isLoading}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="warning"
                                  onClick={handleCancelInlineEdit}
                                  title="Cancel Edit"
                                  disabled={isLoading}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </>
                            ) : (
                              // Show regular action buttons when not in edit mode
                              <>
                                <IconButton 
                                  size="small" 
                                  color="info"
                                  onClick={() => handleViewRecord(record)}
                                  title="View Details"
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={() => handleInlineEdit(record)}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleDeleteRecord(record.no)}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
      )}

      {/* View Record Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Activity Details
          {selectedRecord && (
            <Chip 
              label={`No: ${selectedRecord.no}`} 
              size="small" 
              color="primary"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">No</Typography>
                <Typography variant="body1">{selectedRecord.no}</Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Investor</Typography>
                <Typography variant="body1">{getInvestorName(selectedRecord.investor_id)}</Typography>
                
                <Typography variant="caption" color="textSecondary">ID: {selectedRecord.investor_id}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Park Id</Typography>
                <Typography variant="body1">{selectedRecord.park_id}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Recorded on</Typography>
                <Typography variant="body1">
                  {selectedRecord.recorded_on ? new Date(selectedRecord.recorded_on).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Period</Typography>
                <Typography variant="body1">
                  {selectedRecord.activity_period_start_from ? 
                    new Date(selectedRecord.activity_period_start_from).toLocaleDateString() : 'N/A'}
                  {' to '}
                  {selectedRecord.activity_period_to ? 
                    new Date(selectedRecord.activity_period_to).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Import</Typography>
                <Typography variant="body1">{selectedRecord.import}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Export</Typography>
                <Typography variant="body1">{selectedRecord.export_amount}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Import Substitution</Typography>
                <Typography variant="body1">{selectedRecord.import_substitution}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Linkage</Typography>
                <Typography variant="body1">{selectedRecord.linkage}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Chip 
                  label={selectedRecord.status || 'Request'} 
                  color={selectedRecord.status === 'Approved' ? 'success' : 
                         selectedRecord.status === 'Rejected' ? 'error' : 'warning'}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                <Typography variant="body1">{selectedRecord.description || 'No description provided'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Employment</Typography>
                <Typography variant="body1">
                  Professional Male: {selectedRecord.emp_prof_male || 0}, 
                  Professional Female: {selectedRecord.emp_prof_female || 0}, 
                  Labor Male: {selectedRecord.emp_labor_male || 0}, 
                  Labor Female: {selectedRecord.emp_labor_female || 0}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Created: {selectedRecord.created_at ? new Date(selectedRecord.created_at).toLocaleString() : 'N/A'}
                  {selectedRecord.updated_at && ` | Last Updated: ${new Date(selectedRecord.updated_at).toLocaleString()}`}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          {selectedRecord && (
            <Button 
              variant="contained" 
              onClick={() => {
                handleEditRecord(selectedRecord);
                handleCloseViewDialog();
                showInfoToast("Loading record for editing...");
              }}
            >
              Edit Record
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}